import { api } from './api'
import type { AllodonsLastSync } from '../utils/allodonsSyncStatus'

/**
 * ALLODONS — client des endpoints `/allodons/*`.
 *
 * Deux familles : le raccordement (écran de réglages) et la consultation de
 * l'historique de dons.
 *
 * Le CRM ne touche JAMAIS l'argent : il lit ce qu'AlloDons a encaissé et le
 * rapproche des appels. Aucun endpoint d'écriture vers AlloDons n'existe, et
 * ce n'est pas un choix — leur API est en lecture seule.
 */

/**
 * Santé du raccordement (LOT-ALLODONS-2, L3) — verdict unique du serveur,
 * priorité disconnected > stale > token_expired > token_unknown > healthy.
 * `stale` = aucun don daté depuis ALLODONS_STALE_DAYS (7 par défaut).
 */
export type AllodonsHealth = 'healthy' | 'stale' | 'disconnected' | 'token_expired' | 'token_unknown'

/** État du jeton d'API : `unknown` = présent mais expiration non lue (NULL). */
export type AllodonsTokenStatus = 'missing' | 'valid' | 'expired' | 'unknown'

export interface AllodonsConnection {
  /*
   * #253 — l'association affichée (une ligne de raccordement par
   * association). FACULTATIFS : un serveur d'avant #253 ne les rend pas.
   */
  /** Identifiant (`?connectionId=` des autres routes) ; `null` tant qu'aucune ligne n'existe. */
  id?: string | null
  /** Nom affiché de l'association, `null` = non nommée. */
  label?: string | null
  /** Association par défaut (mode manuel, campagnes sans choix). */
  isDefault?: boolean
  connected: boolean
  orgSlug: string | null
  /**
   * URL à coller dans AlloDons → Développeur → Webhook. `null` tant que le
   * tenant n'a jamais été raccordé : lire la vue ne crée plus de ligne, c'est
   * le bouton « Créer l'adresse » qui l'engendre (LOT-ALLODONS-2, L1).
   */
  webhookUrl: string | null
  hasValidToken: boolean
  tokenStatus: AllodonsTokenStatus
  tokenExpiresAt: string | null
  connectedAt: string | null
  lastSyncAt: string | null
  /**
   * #252 — libellé français SÛR (table fixe du serveur). L'écran ne l'affiche
   * plus : il traduit `lastSync.code` (`components/integrations/AllodonsSyncStatus`).
   */
  lastSyncStatus: string | null
  /** #252 — code stable + compteurs. FACULTATIF : un serveur d'avant le lot ne le rend pas. */
  lastSync?: AllodonsLastSync | null
  lastWebhookAt: string | null
  isActive: boolean
  syncRunning: boolean
  /** Date du dernier don connu du tenant (MAX(donationDate)), ou null. */
  lastDonationAt: string | null
  health: AllodonsHealth
  /*
   * LOT-ALLODONS-3 — signature et relais. Toujours rendus par le serveur du
   * lot, mais FACULTATIFS ici : un serveur d'avant (frontend déployé le
   * premier) ne doit pas faire tomber l'écran (registre §6, champ neuf).
   */
  /** Un secret de signature est enregistré. Le secret lui-même ne sort JAMAIS. */
  hasWebhookSecret?: boolean
  /** Les webhooks sans en-tête `AlloDons-Signature` sont refusés. */
  webhookSignatureRequired?: boolean
  /** Hôte de l'adresse de relais (`hook.eu1.make.com`) — jamais l'adresse entière. */
  relayHost?: string | null
  lastRelayAt?: string | null
  /** `ok:<statut HTTP>` ou `failed:<cause>`. */
  lastRelayStatus?: string | null
  /** #245 — une clé serveur (module de paiement) est enregistrée ; la clé ne sort JAMAIS. */
  hasPayServerKey?: boolean
  /** #245 — 4 derniers caractères de la clé serveur, `null` sans clé. */
  payServerKeyLast4?: string | null
}

/** Corps de `PATCH /allodons/connection/webhook-settings` : champ absent = inchangé, `null` = effacé. */
export interface AllodonsWebhookSettings {
  webhookSecret?: string | null
  webhookSignatureRequired?: boolean
  relayUrl?: string | null
}


export interface AllodonsSyncReport {
  batchId: string
  fetched: number
  created: number
  updated: number
  skipped: number
  pages: number
  reconciled: number
  /** Échéances d'abonnement rattachées à leur souscription (héritage R-1). */
  inherited: number
  /**
   * LOT-256 : fiches « Inconnu » complétées en fin de passe. FACULTATIF : un
   * serveur d'avant le lot ne le rend pas (registre §6, champ neuf).
   */
  enriched?: number
  truncated: boolean
}

/** Identité d'une fiche contact, avant et après complétion (LOT-256). */
export interface ContactIdentitySnapshot {
  name: string
  firstName: string | null
  lastName: string | null
  email: string | null
}

export interface EnrichSample {
  contactId: string
  phone: string
  before: ContactIdentitySnapshot
  after: ContactIdentitySnapshot
}

/** `GET /allodons/contacts/enrich/preview` — simulation, aucune écriture. */
export interface EnrichPreview {
  dryRun: true
  /** Fiches « Inconnu » de l'organisation. */
  candidates: number
  /** Fiches qu'une exécution compléterait. */
  wouldUpdate: number
  skippedNoDonation: number
  skippedAnonymous: number
  skippedNoName: number
  /** Au plus 10 exemples avant/après. */
  sample: EnrichSample[]
}

/** `POST /allodons/contacts/enrich` — exécution réelle. */
export interface EnrichResult {
  dryRun: false
  candidates: number
  /** Fiches réellement écrites. */
  updated: number
  /** Fiches modifiées par quelqu'un d'autre entre la lecture et l'écriture : laissées telles quelles. */
  conflicts: number
  skippedNoDonation: number
  skippedAnonymous: number
  skippedNoName: number
}

export interface DonationRecord {
  id: string
  donorName: string
  donorEmail: string | null
  phone: string | null
  amount: string
  amountEur: string | null
  currency: string
  isRecurring: boolean
  instalmentIndex: number | null
  instalmentTotal: number | null
  donationDate: string
  campaignName: string | null
  paymentMethod: string | null
  category: string | null
  teamName: string | null
  receiptNumber: string | null
  isAnonymous: boolean
  /** #253 — association source du don (FACULTATIF : serveur d'avant #253). */
  connectionId?: string | null
  /**
   * #253 — nom de l'association du don, pour l'étiquette. `null` quand
   * l'organisation n'a qu'une association : rien à distinguer, pas d'étiquette.
   */
  associationLabel?: string | null
  /**
   * #403 (DONS-MANUELS-A, volet serveur) — origine du don : `MANUAL` = saisi
   * à la main (espèces, chèque, virement) par un utilisateur du CRM.
   * FACULTATIFS : serveur d'avant le lot. L'écran arrive au volet B.
   */
  source?: 'ALLODONS_WEBHOOK' | 'ALLODONS_API' | 'MANUAL'
  /** `TO_RECONCILE` = couple manuel ↔ AlloDons en attente d'un humain (un seul des deux compte). */
  reconciliationStatus?: 'NONE' | 'TO_RECONCILE' | 'RECONCILED' | 'DISMISSED'
  /** L'autre don du couple, s'il y en a un. */
  reconciledWithId?: string | null
  /** Utilisateur du CRM à qui le don est attribué (auteur d'une saisie manuelle). */
  agentUserId?: string | null
  /*
   * DONS-MANUELS-A/B — rendus par la projection depuis `5b73ec8e8`, FACULTATIFS
   * pour un serveur d'avant (registre §6, champ neuf) : l'écran se tait sans eux.
   */
  /** Nom de l'auteur d'un don MANUAL (`User.name`, résolu côté serveur) ; `null` sinon. */
  agentUserName?: string | null
  /**
   * Date de SAISIE (ISO) — la fenêtre de 24 h de l'auteur se juge sur elle, pas
   * sur `donationDate` ; absente, l'auteur non admin ne voit pas « Supprimer ».
   */
  createdAt?: string
  /** Fiche contact rattachée (lien de la ligne « à rapprocher » vers la fiche). */
  contactId?: string | null
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * DONS-MANUELS-B (#403, volet écran) — dons reçus HORS AlloDons (espèces,
 * chèque, virement) saisis sur une fiche, couples « à rapprocher », décision,
 * suppression. Les types sont RECOPIÉS de
 * `apps/backend/src/allodons/manual-donations.service.ts` (jamais importés :
 * le frontend ne dépend pas du serveur à la compilation).
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Liste FERMÉE des moyens de paiement d'un don manuel (`MANUAL_PAYMENT_METHODS` du serveur). */
export const MANUAL_PAYMENT_METHODS = ['ESPECES', 'CHEQUE', 'VIREMENT', 'AUTRE'] as const
export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number]

/** Devises proposées à la saisie (le DTO accepte tout code à trois lettres ; l'écran en propose cinq). */
export const MANUAL_DONATION_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'ILS'] as const

/** Fenêtre pendant laquelle l'AUTEUR (non admin) peut supprimer sa saisie (`MANUAL_DELETE_GRACE_MS`). */
export const MANUAL_DELETE_GRACE_MS = 24 * 60 * 60 * 1000

/** Longueur maximale du commentaire à l'écran (le DTO accepte 2 000 ; décision écran : 500). */
export const MANUAL_DONATION_COMMENT_MAX = 500

/** Corps de `POST /allodons/donations/manual` (`CreateManualDonationDto`). */
export interface CreateManualDonationInput {
  contactId: string
  /** Strictement positif, deux décimales au plus. */
  amount: number
  /** Code ISO à trois lettres ; absent = EUR. */
  currency?: string
  paymentMethod: ManualPaymentMethod
  /** ISO 8601 (`AAAA-MM-JJ` suffit) ; absent = maintenant ; jamais dans le futur. */
  donationDate?: string
  comment?: string
  campaignName?: string
  callId?: string
}

/** Avertissement rendu quand la saisie a trouvé un jumeau AlloDons (`ManualDonationWarning`). */
export interface ManualDonationWarning {
  code: 'DONATION_TO_RECONCILE'
  message: string
  /** Le don AlloDons jumeau — pour que l'écran le montre. */
  twin: { id: string; amount: string; currency: string; donationDate: string }
}

/** Réponse 201 de la saisie (`CreateManualDonationResult`). */
export interface CreateManualDonationResult {
  donation: DonationRecord
  warning: ManualDonationWarning | null
}

/** Un couple « à rapprocher » (`ReconcilePair`). */
export interface ReconcilePair {
  /** Le don saisi à la main (suspendu tant que le couple est en attente). */
  manual: DonationRecord
  /** Le don AlloDons jumeau — `null` s'il a disparu (ne devrait pas arriver). */
  allodons: DonationRecord | null
}

/** `GET /allodons/donations/reconcile` (`ReconcileListResult`). */
export interface ReconcileListResult {
  items: ReconcilePair[]
  total: number
  page: number
  perPage: number
  hasMore: boolean
}

/** Décision d'un humain sur un couple (`RECONCILE_DECISIONS`). */
export type ReconcileDecision = 'RECONCILED' | 'DISMISSED'

export interface DonationSummary {
  donationCount: number
  totalEur: number
  firstDonationDate: string | null
  lastDonationDate: string | null
  lastDonationAmount: number | null
  lastDonationCurrency: string | null
  hasRecurring: boolean
}

export interface DonationLookup {
  summary: DonationSummary
  items: DonationRecord[]
  total: number
  hasMore: boolean
}

export interface AllodonsTeam {
  id: number
  name: string
  campaign: string
}

/**
 * Un donateur candidat rendu par la recherche par NOM (S-2).
 *
 * Le « donateur » n'est pas une table : c'est l'agrégat des dons partageant la
 * même identité (nom, téléphone, e-mail). Deux homonymes aux téléphones
 * différents arrivent comme DEUX candidats — c'est l'écran qui fait choisir.
 */
export interface DonorCandidate {
  donorName: string
  phone: string | null
  donorEmail: string | null
  donationCount: number
  totalEur: number
  lastDonationDate: string
}

export interface DonorSearchResult {
  donors: DonorCandidate[]
  hasMore: boolean
}

/**
 * LOT-229 — un donateur AlloDons qu'aucune fiche ne porte (onglet « Donateurs
 * sans fiche »). Un donateur = un numéro ; sans numéro, regroupé par e-mail
 * (listé mais non créable : une fiche exige un numéro).
 */
export interface UnmatchedDonor {
  /** Numéro normalisé, ou `email:<adresse>` pour un donateur sans numéro. */
  key: string
  phone: string | null
  email: string | null
  name: string
  donationCount: number
  totalEur: number
  lastDonationDate: string
  hasActiveSubscription: boolean
  creatable: boolean
  reason: 'NO_PHONE' | 'INVALID_PHONE' | null
}

export interface UnmatchedDonorsPage {
  donors: UnmatchedDonor[]
  page: number
  perPage: number
  /** Donateurs du filtre courant. */
  total: number
  /** Parmi eux, ceux dont la fiche peut être créée. */
  creatable: number
  /** Tous les donateurs sans fiche du tenant (compteur de l'onglet). */
  unmatchedTotal: number
  /** Groupes de dons sans numéro ni e-mail : non listés. */
  uncreatable: number
  hasMore: boolean
}

export type CreateDonorContactFailure =
  | 'NO_DONATIONS'
  | 'INVALID_PHONE'
  | 'NAME_REQUIRED'
  | 'INVALID_CONTACT_FIELD'
  | 'ERROR'

export interface CreateDonorContactsReport {
  created: number
  skipped: number
  failed: Array<{ phone: string; reason: CreateDonorContactFailure }>
  contacts: Array<{ phone: string; contactId: string; name: string }>
}

/**
 * Portée d'organisation.
 *
 * Un raccordement AlloDons appartient à UNE association. Le compte système
 * (super-admin) n'ayant pas d'organisation à lui, `@TenantId()` renvoie `null`
 * côté serveur à moins qu'un `tenantId` ne soit passé en requête — même
 * mécanique que `services/monitoring.ts`. L'écran choisit donc l'association,
 * et la transmet à chaque appel.
 */
/** Options de transport d'une lecture d'historique de dons. */
export interface DonationLookupRequestOptions {
  /** Requête de fond : l'intercepteur axios ne toaste pas son erreur. */
  background?: boolean
  /**
   * ALLODONS-FIN-B (#255) — compte système SEULEMENT : l'organisation du
   * contact consulté (`?tenantId=`, lu par `@TenantId()` pour lui seul et
   * ignoré pour tout autre utilisateur). Absent = l'appel historique.
   */
  tenantId?: string
}

const BACKGROUND_HEADERS = { 'X-Background-Request': 'true' } as const

/** Paramètres d'une lecture d'historique : la pagination, plus `tenantId` s'il est désigné. */
function lookupParams(params: { page?: number; perPage?: number }, options: DonationLookupRequestOptions) {
  return options.tenantId ? { ...params, tenantId: options.tenantId } : params
}

/**
 * #154 — ce que l'export CSV rend à l'écran : le fichier, son nom (serveur,
 * `Content-Disposition`), le nombre de lignes et le plafond.
 */
export interface DonationsCsvExport {
  blob: Blob
  fileName: string
  /** Lignes du fichier (`X-Export-Rows`) ; `null` si le serveur ne l'a pas dit. */
  rows: number | null
  /** Le plafond du serveur a été atteint : le fichier s'arrête, la suite est dans `nextCursor`. */
  truncated: boolean
  nextCursor: string | null
}

export interface DonationsCsvExportQuery {
  /** Premier jour inclus, `AAAA-MM-JJ`. */
  from: string
  /** Dernier jour inclus, `AAAA-MM-JJ`. */
  to: string
  /** Compte système : l'organisation désignée. */
  tenantId?: string
  /** Suite d'un export plafonné (`nextCursor` du précédent). */
  cursor?: string
}

/** Nom de fichier d'un `Content-Disposition: attachment; filename="…"` (ou `filename*=UTF-8''…`), sinon `null`. */
export function fileNameFromContentDisposition(header: unknown): string | null {
  if (typeof header !== 'string') return null
  const encoded = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(header)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim()) || null
    } catch {
      // encodage illisible : on tente la forme simple
    }
  }
  const plain = /filename="([^"]*)"/.exec(header) ?? /filename=([^;]+)/.exec(header)
  const name = plain?.[1]?.trim() ?? ''
  return name || null
}

/**
 * `tenantId` : compte système seulement. `connectionId` (#253) : l'association
 * visée — absente, le serveur prend l'association par défaut.
 */
function scope(tenantId?: string, connectionId?: string | null) {
  const params = {
    ...(tenantId ? { tenantId } : {}),
    ...(connectionId ? { connectionId } : {}),
  }
  return Object.keys(params).length > 0 ? { params } : {}
}

const AllodonsService = {
  getConnection: async (tenantId?: string, connectionId?: string | null): Promise<AllodonsConnection> => {
    const { data } = await api.get('/allodons/connection', scope(tenantId, connectionId))
    return data
  },

  setToken: async (
    token: string,
    orgSlug?: string,
    tenantId?: string,
    connectionId?: string | null,
  ): Promise<AllodonsConnection> => {
    const { data } = await api.post(
      '/allodons/connection/token',
      { token, orgSlug },
      scope(tenantId, connectionId),
    )
    return data
  },

  rotateWebhook: async (tenantId?: string, connectionId?: string | null): Promise<AllodonsConnection> => {
    const { data } = await api.post(
      '/allodons/connection/rotate-webhook',
      {},
      scope(tenantId, connectionId),
    )
    return data
  },

  updateWebhookSettings: async (
    settings: AllodonsWebhookSettings,
    tenantId?: string,
    connectionId?: string | null,
  ): Promise<AllodonsConnection> => {
    const { data } = await api.patch(
      '/allodons/connection/webhook-settings',
      settings,
      scope(tenantId, connectionId),
    )
    return data
  },

  /**
   * #253 — « Ajouter une association » : une nouvelle ligne de raccordement
   * (sa propre adresse de webhook). La vue rendue porte son `id`, que l'écran
   * de raccordement reçoit ensuite en `?connectionId=`.
   */
  createAssociation: async (
    input: { label?: string; orgSlug?: string },
    tenantId?: string,
  ): Promise<AllodonsConnection> => {
    const { data } = await api.post('/allodons/connections', input, scope(tenantId))
    return data
  },

  /** #253 — renommer une association (vide = nom effacé). */
  updateLabel: async (
    label: string,
    connectionId?: string | null,
    tenantId?: string,
  ): Promise<AllodonsConnection> => {
    const { data } = await api.patch(
      '/allodons/connection/label',
      { label },
      scope(tenantId, connectionId),
    )
    return data
  },

  /**
   * #245 — coller (ou effacer, `null`) la clé serveur AlloDons du module de
   * paiement. Chiffrée au repos côté serveur ; 503 `ALLODONS_PAY_CIPHER_NOT_CONFIGURED`
   * si la variable de chiffrement n'est pas posée (rien n'est alors stocké).
   */
  setPayServerKey: async (
    payServerKey: string | null,
    tenantId?: string,
    connectionId?: string | null,
  ): Promise<AllodonsConnection> => {
    const { data } = await api.patch(
      '/allodons/connection/pay-server-key',
      { payServerKey },
      // Requête de FOND : le 503 (chiffrement non configuré) est rendu DANS la carte
      // par `PayServerKeyForm` ; l'intercepteur ne doit pas le toaster en plus.
      { ...scope(tenantId, connectionId), headers: BACKGROUND_HEADERS },
    )
    return data
  },

  /** #253 — désigner l'association par défaut (mode manuel, campagnes sans choix). */
  setDefault: async (connectionId: string, tenantId?: string): Promise<AllodonsConnection> => {
    const { data } = await api.post('/allodons/connection/default', {}, scope(tenantId, connectionId))
    return data
  },

  /**
   * #270 — modifier SEUL le slug de la page de collecte (carte AlloDons de la
   * page Intégrations), sans recoller de jeton. Vide = effacé ; une adresse
   * complète collée est ramenée à son dernier segment par le serveur.
   */
  updateOrgSlug: async (
    orgSlug: string,
    tenantId?: string,
    connectionId?: string | null,
  ): Promise<AllodonsConnection> => {
    const { data } = await api.patch(
      '/allodons/connection/org-slug',
      { orgSlug },
      scope(tenantId, connectionId),
    )
    return data
  },

  backfill: async (tenantId?: string, connectionId?: string | null): Promise<AllodonsSyncReport> => {
    const { data } = await api.post('/allodons/sync/backfill', {}, scope(tenantId, connectionId))
    return data
  },

  resync: async (
    sinceDays: number,
    tenantId?: string,
    connectionId?: string | null,
  ): Promise<AllodonsSyncReport> => {
    const { data } = await api.post(
      '/allodons/sync/resync',
      { sinceDays },
      scope(tenantId, connectionId),
    )
    return data
  },

  /**
   * LOT-256 — compléter les fiches « Inconnu » depuis les dons. La simulation
   * ne touche rien ; l'exécution écrit nom/prénom (et l'e-mail s'il est vide)
   * sans jamais écraser une valeur saisie. 409 si une exécution est en cours.
   */
  enrichPreview: async (tenantId?: string): Promise<EnrichPreview> => {
    const { data } = await api.get('/allodons/contacts/enrich/preview', scope(tenantId))
    return data
  },

  enrichRun: async (tenantId?: string): Promise<EnrichResult> => {
    const { data } = await api.post('/allodons/contacts/enrich', {}, scope(tenantId))
    return data
  },

  listTeams: async (tenantId?: string): Promise<AllodonsTeam[]> => {
    const { data } = await api.get('/allodons/teams', scope(tenantId))
    return data.teams ?? []
  },

  /**
   * #225 / L-NAV-7 — rattacher un agent à son équipe AlloDons (fiche
   * `/equipe/:userId`). `tenantId` : tenant DE L'AGENT, désigné par le seul
   * compte système (la route refuse un tenant `null`, et son `updateMany`
   * filtré par tenant interdit l'agent d'une autre organisation).
   */
  linkAgentTeam: async (userId: string, allodonsTeamId: number, tenantId?: string): Promise<void> => {
    await api.post(`/allodons/users/${encodeURIComponent(userId)}/team`, { allodonsTeamId }, scope(tenantId))
  },

  /**
   * Historique par téléphone — l'entrée du chemin chaud.
   *
   * Le numéro est encodé : il commence par « + », qui vaut espace dans un
   * segment d'URL non échappé. `background` : cf. `byContact` (l'onglet Dons
   * de la scène le pose ; l'écran Historique donateur garde l'appel nu).
   */
  byPhone: async (
    phone: string,
    params: { page?: number; perPage?: number } = {},
    options: DonationLookupRequestOptions = {},
  ): Promise<DonationLookup> => {
    const scoped = lookupParams(params, options)
    const { data } = await api.get(
      `/allodons/donations/by-phone/${encodeURIComponent(phone)}`,
      options.background ? { params: scoped, headers: BACKGROUND_HEADERS } : { params: scoped },
    )
    return data
  },

  /**
   * Historique d'un contact du CRM — les dons RATTACHÉS à la fiche
   * (`DonationHistory.contactId`), consommé par le bloc « Dons » de la fiche
   * (LOT-DONS-AGENT-1, #228) et l'onglet Dons de la scène.
   *
   * `background` (opt-in) pose `X-Background-Request` : l'intercepteur ne
   * toaste plus l'erreur, c'est la surface qui décide (se taire, ou un message
   * court en appel). Sans l'option, l'appel garde sa forme historique.
   */
  byContact: async (
    contactId: string,
    params: { page?: number; perPage?: number } = {},
    options: DonationLookupRequestOptions = {},
  ): Promise<DonationLookup> => {
    const scoped = lookupParams(params, options)
    const { data } = await api.get(
      `/allodons/donations/by-contact/${encodeURIComponent(contactId)}`,
      options.background ? { params: scoped, headers: BACKGROUND_HEADERS } : { params: scoped },
    )
    return data
  },

  /**
   * #154 — export CSV des dons d'une période (ADMIN). Requête de FOND : le
   * composant affiche lui-même l'erreur. Le fichier arrive en `Blob` ; son nom
   * vient du serveur (`Content-Disposition`, exposé au navigateur), le plafond
   * des en-têtes `X-Export-*` (axios les rend en minuscules).
   */
  exportDonationsCsv: async (query: DonationsCsvExportQuery): Promise<DonationsCsvExport> => {
    const response = await api.get('/allodons/donations/export.csv', {
      params: {
        from: query.from,
        to: query.to,
        ...(query.tenantId ? { tenantId: query.tenantId } : {}),
        ...(query.cursor ? { cursor: query.cursor } : {}),
      },
      responseType: 'blob',
      headers: BACKGROUND_HEADERS,
    })
    const headers = (response.headers ?? {}) as Record<string, unknown>
    const rows = Number(headers['x-export-rows'])
    const nextCursor = headers['x-export-next-cursor']
    return {
      blob: response.data instanceof Blob ? response.data : new Blob([response.data ?? ''], { type: 'text/csv' }),
      fileName:
        fileNameFromContentDisposition(headers['content-disposition']) ??
        `dons-allodons-${query.from}-au-${query.to}.csv`,
      rows: Number.isInteger(rows) && rows >= 0 ? rows : null,
      truncated: String(headers['x-export-truncated']).toLowerCase() === 'true',
      nextCursor: typeof nextCursor === 'string' && nextCursor ? nextCursor : null,
    }
  },

  /** Historique par e-mail — le repli quand un candidat n'a pas de téléphone. */
  byEmail: async (
    email: string,
    params: { page?: number; perPage?: number } = {},
  ): Promise<DonationLookup> => {
    const { data } = await api.get(
      `/allodons/donations/by-email/${encodeURIComponent(email)}`,
      { params },
    )
    return data
  },

  /**
   * Donateurs dont le nom contient le fragment (S-2) — insensible à la casse
   * et aux accents, agrégé par donateur, borné à 20 candidats.
   */
  searchDonors: async (
    q: string,
    params: { page?: number; perPage?: number } = {},
  ): Promise<DonorSearchResult> => {
    const { data } = await api.get('/allodons/donations/search-donors', {
      params: { q, ...params },
    })
    return data
  },

  /**
   * LOT-229 — donateurs sans fiche, paginés. Requête de FOND : l'onglet
   * affiche lui-même son état d'erreur (pas de toast en double).
   */
  listUnmatchedDonors: async (
    /** `tenantId` (ALLODONS-FIN-B, #255) : compte système seulement — l'organisation désignée. */
    params: { q?: string; page?: number; perPage?: number; tenantId?: string } = {},
  ): Promise<unknown> => {
    const { data } = await api.get('/allodons/donors/unmatched', {
      params,
      headers: BACKGROUND_HEADERS,
    })
    return data
  },

  /** Numéros créables du filtre (alimente « Tout créer »). `tenantId` : compte système seulement. */
  listUnmatchedDonorPhones: async (q?: string, tenantId?: string): Promise<string[]> => {
    const { data } = await api.get('/allodons/donors/unmatched/phones', {
      params: { ...(q ? { q } : {}), ...(tenantId ? { tenantId } : {}) },
    })
    return Array.isArray(data?.phones)
      ? data.phones.filter((phone: unknown): phone is string => typeof phone === 'string')
      : []
  },

  /**
   * Crée les fiches de ces donateurs (≤ 200 numéros) et y rattache leurs dons.
   * `tenantId` (compte système) part en REQUÊTE, pas dans le corps : le DTO du
   * corps est en liste blanche (`forbidNonWhitelisted`), `@TenantId()` lit
   * `?tenantId=` en premier.
   */
  createContactsFromDonors: async (phones: string[], tenantId?: string): Promise<CreateDonorContactsReport> => {
    const { data } = await api.post(
      '/allodons/donors/unmatched/create-contacts',
      { phones },
      ...(tenantId ? [{ params: { tenantId } }] : []),
    )
    return {
      created: Number(data?.created ?? 0),
      skipped: Number(data?.skipped ?? 0),
      failed: Array.isArray(data?.failed) ? data.failed : [],
      contacts: Array.isArray(data?.contacts) ? data.contacts : [],
    }
  },
  // ── DONS-MANUELS-B (#403) ─────────────────────────────────────────────────
  //
  // `tenantId` : compte système SEULEMENT (l'organisation de la fiche ou celle
  // désignée par le sélecteur), en REQUÊTE — jamais dans le corps (le DTO est
  // en liste blanche, registre §57). Requêtes de FOND : la modale, le bloc et
  // l'onglet rendent eux-mêmes leur erreur (pas de toast en double).

  /** Enregistrer un don reçu hors AlloDons sur une fiche — 201 `{ donation, warning }`. */
  createManualDonation: async (
    input: CreateManualDonationInput,
    tenantId?: string,
  ): Promise<CreateManualDonationResult> => {
    const { data } = await api.post('/allodons/donations/manual', input, {
      ...scope(tenantId),
      headers: BACKGROUND_HEADERS,
    })
    return data
  },

  /** Couples « à rapprocher » du tenant, paginés (`total` sert de compteur d'onglet). */
  listDonationsToReconcile: async (
    params: { page?: number; perPage?: number; tenantId?: string } = {},
  ): Promise<ReconcileListResult> => {
    const { data } = await api.get('/allodons/donations/reconcile', {
      params,
      headers: BACKGROUND_HEADERS,
    })
    return data
  },

  /** Trancher un couple (ADMIN) : `:id` = l'un ou l'autre don du couple. */
  decideReconciliation: async (
    donationId: string,
    decision: ReconcileDecision,
    tenantId?: string,
  ): Promise<unknown> => {
    const { data } = await api.post(
      `/allodons/donations/reconcile/${encodeURIComponent(donationId)}`,
      { decision },
      { ...scope(tenantId), headers: BACKGROUND_HEADERS },
    )
    return data
  },

  /** Supprimer un don saisi à la main (ADMIN, ou son auteur dans les 24 h). */
  deleteManualDonation: async (donationId: string, tenantId?: string): Promise<void> => {
    await api.delete(`/allodons/donations/manual/${encodeURIComponent(donationId)}`, {
      ...scope(tenantId),
      headers: BACKGROUND_HEADERS,
    })
  },
}

export default AllodonsService
